'use client';

/**
 * EnvironmentConfig Component
 *
 * Displays environment configuration variables organized by category.
 * Shows configuration status, masked values, and metadata for server/client vars.
 */

import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  Center,
  Inline,
  Spinner,
  Stack,
  Text,
} from '@cortex/ui';
import { ChevronDown, ChevronRight, Lock } from 'lucide-react';
import * as React from 'react';

import { useApi } from '@/hooks/use-api';

const KEY_MAX_LENGTH = 50;

function truncate(text: string, maxLength: number): string {
  return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
}

interface ConfigItem {
  key: string;
  displayValue: string;
  isSet: boolean;
  isSecret: boolean;
  category: string;
  scope: 'server' | 'client';
}

interface ConfigGroup {
  category: string;
  label: string;
  items: ConfigItem[];
  setCount: number;
  totalCount: number;
}

export function EnvironmentConfig() {
  const getApi = useApi();
  const [groups, setGroups] = React.useState<ConfigGroup[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [expandedCategories, setExpandedCategories] = React.useState<
    Set<string>
  >(new Set());

  const fetchConfig = React.useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const api = await getApi();
      const response = await api.admin.config.$get();

      if (!response.ok) {
        throw new Error('Failed to fetch configuration');
      }

      const json = await response.json();
      if ('data' in json) {
        setGroups((json.data as unknown as ConfigGroup[]) || []);
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to load configuration'
      );
    } finally {
      setLoading(false);
    }
  }, [getApi]);

  React.useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const toggleCategory = (category: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  if (loading) {
    return (
      <Card className="p-6">
        <Inline gap="sm">
          <Spinner size="sm" variant="muted" />
          <Text color="muted">Loading configuration...</Text>
        </Inline>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="p-6">
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchConfig}
            className="mt-2"
          >
            Retry
          </Button>
        </Alert>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      {/* Header */}
      <Stack gap="none" className="mb-6">
        <Text size="lg" weight="semibold">
          Environment Configuration
        </Text>
        <Text color="muted" size="sm" className="mt-1">
          Review server and client environment variables
        </Text>
      </Stack>

      {/* Config Groups */}
      <Stack gap="sm">
        {groups.map((group) => {
          const isExpanded = expandedCategories.has(group.category);

          return (
            <Card key={group.category} className="bg-surface-2">
              {/* Group Header */}
              <Button
                variant="ghost"
                onClick={() => toggleCategory(group.category)}
                className="flex h-auto w-full items-center justify-between rounded-none p-4 text-left"
              >
                <Inline gap="sm">
                  {isExpanded ? (
                    <ChevronDown className="text-foreground/60 h-4 w-4" />
                  ) : (
                    <ChevronRight className="text-foreground/60 h-4 w-4" />
                  )}
                  <Text weight="medium">{group.label}</Text>
                  <Badge variant="outline">
                    {group.setCount}/{group.totalCount} set
                  </Badge>
                </Inline>
              </Button>

              {/* Group Items */}
              {isExpanded && (
                <Stack className="border-border border-t" gap="none">
                  <Stack className="divide-border divide-y" gap="none">
                    {group.items.map((item) => (
                      <Stack key={item.key} className="p-4" gap="none">
                        <Inline align="start" justify="between" gap="md">
                          {/* Variable Info */}
                          <Stack className="min-w-0 flex-1" gap="none">
                            <Inline gap="xs" className="mb-1">
                              <Text
                                size="sm"
                                weight="medium"
                                className="font-mono"
                              >
                                {truncate(item.key, KEY_MAX_LENGTH)}
                              </Text>
                              {item.isSecret && (
                                <Lock className="text-foreground/60 h-3 w-3" />
                              )}
                            </Inline>
                            <Text
                              color="muted"
                              size="xs"
                              className="font-mono break-all"
                            >
                              {item.displayValue}
                            </Text>
                          </Stack>

                          {/* Badges */}
                          <Inline gap="xs" className="flex-shrink-0">
                            <Badge
                              variant={
                                item.scope === 'server' ? 'info' : 'secondary'
                              }
                            >
                              {item.scope === 'server' ? 'Server' : 'Client'}
                            </Badge>
                            <Badge variant={item.isSet ? 'success' : 'warning'}>
                              {item.isSet ? 'Configured' : 'Not set'}
                            </Badge>
                          </Inline>
                        </Inline>
                      </Stack>
                    ))}
                  </Stack>
                </Stack>
              )}
            </Card>
          );
        })}
      </Stack>

      {/* Empty State */}
      {groups.length === 0 && (
        <Center minHeight="12rem">
          <Text color="muted" align="center">
            No configuration variables found
          </Text>
        </Center>
      )}
    </Card>
  );
}
